/**
 * @file     BuyInputsCard.tsx
 * @purpose  Card holding all "buy side" inputs grouped under Property and Costs sub-sections, including a synced down payment $/% slider.
 */
'use client';

import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { NumericField } from './NumericField';
import { DashboardSlider } from '../_shared/DashboardSlider';
import type { RentVsBuyInputs } from './rentVsBuyMath';

type Props = {
  inputs: RentVsBuyInputs;
  patch: (next: Partial<RentVsBuyInputs>) => void;
  /** When true, force single-column field layout. Used when the card sits in a narrow rail. */
  dense?: boolean;
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => {
  const { theme } = useAppTheme();
  return (
    <Typography
      sx={{
        fontSize: 11,
        color: theme.semanticColors.textMuted,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        mb: 1.25,
      }}
    >
      {children}
    </Typography>
  );
};

export const BuyInputsCard = ({ inputs, patch, dense = false }: Props) => {
  const { theme } = useAppTheme();
  // When dense (rail mode), use 1-col above the workspace collapse breakpoint (1200px+),
  // and 2-col below it where the rail expands to full screen width on tablet/mobile.
  const fieldGridSx = dense
    ? {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 2,
        '@media (min-width: 1200px)': { gridTemplateColumns: '1fr' },
        '@media (max-width: 599px)': { gridTemplateColumns: '1fr' },
      }
    : {
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2,
      };
  const downPct = useMemo(() => {
    if (inputs.homePrice <= 0) return 0;
    return Math.round((inputs.downPayment / inputs.homePrice) * 1000) / 10;
  }, [inputs.downPayment, inputs.homePrice]);

  return (
    <Box
      sx={{
        bgcolor: theme.semanticColors.surfaceCard,
        borderRadius: `${theme.layout.radius.md}px`,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Typography
        sx={{
          fontFamily: theme.typography.fontFamilyHeading,
          fontSize: theme.typography.h4,
          fontWeight: 600,
          color: theme.semanticColors.textPrimary,
          letterSpacing: '-0.01em',
          mb: 0.5,
        }}
      >
        Buy side
      </Typography>
      <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textSecondary, mb: 2.5 }}>
        Your costs and assumptions if you buy.
      </Typography>

      <SectionHeading>Property</SectionHeading>
      <Box sx={fieldGridSx}>
        <NumericField
          label="Home price"
          value={inputs.homePrice}
          onChange={(v) => {
            const newPrice = Math.max(0, v);
            const pct = inputs.homePrice > 0 ? inputs.downPayment / inputs.homePrice : 0.2;
            patch({
              homePrice: newPrice,
              downPayment: Math.round(newPrice * pct),
            });
          }}
          prefix="$"
          min={0}
        />
        <NumericField
          label="Down payment"
          value={inputs.downPayment}
          onChange={(v) => patch({ downPayment: Math.max(0, Math.min(v, inputs.homePrice)) })}
          prefix="$"
          min={0}
          helper={`${downPct.toFixed(1)}% of home price`}
        />
      </Box>

      <Box sx={{ mt: 1, mb: 0.5 }}>
        <DashboardSlider
          value={Math.min(50, Math.max(0, downPct))}
          min={0}
          max={50}
          step={0.5}
          marks={[
            { value: 5, label: '5%' },
            { value: 10, label: '10%' },
            { value: 20, label: '20%' },
          ]}
          onChange={(_, v) => {
            const pct = Array.isArray(v) ? v[0] : v;
            patch({ downPayment: Math.round((inputs.homePrice * pct) / 100) });
          }}
          aria-label="Down payment percent"
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}%`}
        />
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <SectionHeading>Costs</SectionHeading>
        <Box sx={fieldGridSx}>
          <NumericField
            label="Mortgage rate"
            value={inputs.mortgageRatePct}
            onChange={(v) => patch({ mortgageRatePct: Math.max(0, Math.min(v, 25)) })}
            suffix="%"
            step={0.05}
            min={0}
            max={25}
          />
          <NumericField
            label="Amortization"
            value={inputs.amortizationYears}
            onChange={(v) => patch({ amortizationYears: Math.max(1, Math.min(Math.round(v), 40)) })}
            suffix="years"
            step={1}
            min={1}
            max={40}
          />
          <NumericField
            label="Property tax (annual)"
            value={inputs.propertyTaxAnnual}
            onChange={(v) => patch({ propertyTaxAnnual: Math.max(0, v) })}
            prefix="$"
            min={0}
          />
          <NumericField
            label="Strata, maintenance (monthly)"
            value={inputs.strataMonthly}
            onChange={(v) => patch({ strataMonthly: Math.max(0, v) })}
            prefix="$"
            min={0}
          />
          <NumericField
            label="Home appreciation"
            value={inputs.appreciationPct}
            onChange={(v) => patch({ appreciationPct: Math.max(-10, Math.min(v, 20)) })}
            suffix="% / yr"
            step={0.1}
          />
          <NumericField
            label="Closing costs"
            value={inputs.closingCosts}
            onChange={(v) => patch({ closingCosts: Math.max(0, v) })}
            prefix="$"
            min={0}
            helper="PTT, legal, inspection"
          />
          <NumericField
            label="Selling costs"
            value={inputs.sellingCostsPct}
            onChange={(v) => patch({ sellingCostsPct: Math.max(0, Math.min(v, 15)) })}
            suffix="% of sale"
            step={0.1}
            min={0}
            max={15}
          />
        </Box>
      </Box>
    </Box>
  );
};
